import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, Save, Building2 } from "lucide-react";
import { consoleApi } from "@/lib/console-auth";
import "@/styles/console.css";

/* ─────────────────────────────────────────────────────────────────────────────
   TenantsManager — multi-tenant configuration surface.
   Each tenant has metadata · contact routing · brand · 1+ tiers.
   Each tier has pillar rights · club access · monthly quota · perks · upsell.
   ───────────────────────────────────────────────────────────────────────── */

const ALL_PILLARS = [
    "Advisory", "Care", "Club", "Dine", "Enjoy", "Escape",
    "Exclusive", "Fit", "Stay", "Transport", "Work",
];

export default function TenantsManager() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawer, setDrawer] = useState(null);  // null | {mode: "edit"|"new", tenant?}
    const [refresher, setRefresher] = useState(0);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await consoleApi.get("/console/tenants");
                setTenants(data.tenants || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [refresher]);

    const onNew = () => setDrawer({ mode: "new", tenant: newTenantTemplate() });
    const onEdit = (t) => setDrawer({ mode: "edit", tenant: deepClone(t) });
    const onDelete = async (t) => {
        if (!window.confirm(`Soft-delete '${t.name}'? It will be marked inactive.`)) return;
        try {
            await consoleApi.delete(`/console/tenants/${t.tenant_id}`);
            setRefresher((r) => r + 1);
        } catch (e) {
            alert(e.response?.data?.detail || e.message);
        }
    };
    const onSaved = () => {
        setDrawer(null);
        setRefresher((r) => r + 1);
    };

    return (
        <div data-testid="tenants-manager">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="console-eyebrow">Live tenants</p>
                </div>
                <button onClick={onNew} className="console-btn-primary" data-testid="tenants-new-btn">
                    <Plus size={14} className="inline mr-2" /> New tenant
                </button>
            </div>

            <div className="console-card overflow-x-auto" data-testid="tenants-table-card">
                {loading ? (
                    <p className="p-6 console-eyebrow" data-testid="tenants-loading">Loading tenants…</p>
                ) : tenants.length === 0 ? (
                    <p className="p-6 console-eyebrow">No tenants yet.</p>
                ) : (
                    <table className="console-table" data-testid="tenants-table">
                        <thead>
                            <tr>
                                <th>Tenant</th>
                                <th>Tier(s)</th>
                                <th>Pillars</th>
                                <th>Club access</th>
                                <th>Quota / month</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map((t) => (
                                <tr key={t.tenant_id} data-testid={`tenant-row-${t.tenant_id}`}>
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <Building2 size={16} style={{ color: "var(--console-brass)" }} />
                                            <div>
                                                <p style={{ fontWeight: 600 }}>{t.name}</p>
                                                <p className="console-eyebrow mt-[2px]">{t.tenant_id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-wrap gap-1">
                                            {(t.tiers || []).map((tier) => (
                                                <span
                                                    key={tier.tier_id}
                                                    className="console-pill console-pill-brass"
                                                    data-testid={`tenant-${t.tenant_id}-tier-${tier.tier_id}`}
                                                >
                                                    {tier.name}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ color: "var(--console-text-muted)" }}>
                                            {summarisePillarsAcrossTiers(t.tiers)}
                                        </span>
                                    </td>
                                    <td>
                                        {hasAnyClubAccess(t.tiers) ? (
                                            <span className="console-pill console-pill-active">Yes (any tier)</span>
                                        ) : (
                                            <span className="console-pill console-pill-muted">No</span>
                                        )}
                                    </td>
                                    <td>
                                        <span style={{ color: "var(--console-text-muted)", fontSize: 12 }}>
                                            {summariseQuotas(t.tiers)}
                                        </span>
                                    </td>
                                    <td>
                                        {t.active ? (
                                            <span className="console-pill console-pill-active">Active</span>
                                        ) : (
                                            <span className="console-pill console-pill-muted">Inactive</span>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => onEdit(t)}
                                            className="console-btn-ghost mr-2"
                                            data-testid={`tenant-edit-${t.tenant_id}`}
                                        >
                                            <Edit3 size={12} className="inline mr-1" /> Edit
                                        </button>
                                        {t.tenant_id !== "lesconcierge" && (
                                            <button
                                                onClick={() => onDelete(t)}
                                                className="console-btn-ghost"
                                                style={{ color: "var(--console-danger)" }}
                                                data-testid={`tenant-delete-${t.tenant_id}`}
                                            >
                                                <Trash2 size={12} className="inline mr-1" /> Soft-delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {drawer && (
                <TenantDrawer
                    mode={drawer.mode}
                    initialTenant={drawer.tenant}
                    onClose={() => setDrawer(null)}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */

function TenantDrawer({ mode, initialTenant, onClose, onSaved }) {
    const [t, setT] = useState(initialTenant);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const setField = (path, value) => {
        setT((prev) => {
            const next = deepClone(prev);
            setByPath(next, path, value);
            return next;
        });
    };

    const onSave = async () => {
        setError("");
        setSaving(true);
        try {
            if (mode === "new") {
                await consoleApi.post("/console/tenants", t);
            } else {
                await consoleApi.patch(`/console/tenants/${t.tenant_id}`, {
                    name: t.name,
                    active: t.active,
                    contact: t.contact,
                    brand: t.brand,
                    default_tier: t.default_tier,
                    tiers: t.tiers,
                    notes: t.notes,
                });
            }
            onSaved();
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    const addTier = () => {
        const next = deepClone(t);
        const existing = (next.tiers || []).map((x) => x.tier_id);
        let suffix = "new", n = 1;
        while (existing.includes(suffix)) { suffix = `new_${n++}`; }
        next.tiers = [...(next.tiers || []), {
            tier_id: suffix, name: "New tier",
            pillars: [...ALL_PILLARS], club_access: false,
            monthly_quota: null, perks: [], upsell_to: null,
        }];
        if (!next.default_tier) next.default_tier = suffix;
        setT(next);
    };

    const removeTier = (tier_id) => {
        const next = deepClone(t);
        next.tiers = (next.tiers || []).filter((x) => x.tier_id !== tier_id);
        if (next.default_tier === tier_id && next.tiers[0]) {
            next.default_tier = next.tiers[0].tier_id;
        }
        setT(next);
    };

    return (
        <div className="console-modal-backdrop" onClick={onClose} data-testid="tenant-drawer">
            <div className="console-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="console-eyebrow">{mode === "new" ? "New tenant" : "Edit tenant"}</p>
                        <h3 className="mt-1" style={{ fontFamily: "Georgia, serif", fontSize: 24 }}>
                            {t.name || "Untitled"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="console-btn-ghost" data-testid="tenant-drawer-close">
                        <X size={14} />
                    </button>
                </div>

                {/* Basics */}
                <Section title="Basics">
                    <Field label="Tenant ID" hint="Lower-case slug · cannot be changed after creation">
                        <input
                            className="console-input"
                            value={t.tenant_id}
                            onChange={(e) => setField("tenant_id", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                            disabled={mode === "edit"}
                            data-testid="tenant-field-id"
                        />
                    </Field>
                    <Field label="Name">
                        <input
                            className="console-input"
                            value={t.name}
                            onChange={(e) => setField("name", e.target.value)}
                            data-testid="tenant-field-name"
                        />
                    </Field>
                    <Field label="Status">
                        <label className="flex items-center gap-3" style={{ fontSize: 13 }}>
                            <input
                                type="checkbox"
                                checked={!!t.active}
                                onChange={(e) => setField("active", e.target.checked)}
                                className="console-switch"
                                data-testid="tenant-field-active"
                            />
                            <span>{t.active ? "Active" : "Inactive"}</span>
                        </label>
                    </Field>
                </Section>

                {/* Contact */}
                <Section title="Contact routing">
                    <Field label="Email">
                        <input
                            className="console-input"
                            value={t.contact?.email || ""}
                            onChange={(e) => setField("contact.email", e.target.value)}
                            data-testid="tenant-field-email"
                        />
                    </Field>
                    <Field label="Phone">
                        <input
                            className="console-input"
                            value={t.contact?.phone || ""}
                            onChange={(e) => setField("contact.phone", e.target.value)}
                            data-testid="tenant-field-phone"
                        />
                    </Field>
                    <Field label="WhatsApp number">
                        <input
                            className="console-input"
                            value={t.contact?.whatsapp_number || ""}
                            onChange={(e) => setField("contact.whatsapp_number", e.target.value)}
                            data-testid="tenant-field-whatsapp"
                        />
                    </Field>
                    <Field label="WhatsApp business ID">
                        <input
                            className="console-input"
                            value={t.contact?.whatsapp_business_id || ""}
                            onChange={(e) => setField("contact.whatsapp_business_id", e.target.value)}
                            data-testid="tenant-field-wabaid"
                        />
                    </Field>
                    <Field label="Escalation email">
                        <input
                            className="console-input"
                            value={t.contact?.escalation_email || ""}
                            onChange={(e) => setField("contact.escalation_email", e.target.value)}
                            data-testid="tenant-field-escalation"
                        />
                    </Field>
                    <Field label="Schedule calendar URL">
                        <input
                            className="console-input"
                            value={t.contact?.schedule_calendar_url || ""}
                            onChange={(e) => setField("contact.schedule_calendar_url", e.target.value)}
                            data-testid="tenant-field-calendar"
                        />
                    </Field>
                </Section>

                {/* Brand */}
                <Section title="Brand">
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Primary">
                            <input
                                className="console-input"
                                value={t.brand?.primary || ""}
                                onChange={(e) => setField("brand.primary", e.target.value)}
                                placeholder="#0d1a33"
                                data-testid="tenant-field-brand-primary"
                            />
                        </Field>
                        <Field label="Accent">
                            <input
                                className="console-input"
                                value={t.brand?.accent || ""}
                                onChange={(e) => setField("brand.accent", e.target.value)}
                                placeholder="#c9a96a"
                                data-testid="tenant-field-brand-accent"
                            />
                        </Field>
                        <Field label="Display mark">
                            <input
                                className="console-input"
                                value={t.brand?.display_mark || "Concierge®"}
                                onChange={(e) => setField("brand.display_mark", e.target.value)}
                                data-testid="tenant-field-brand-mark"
                            />
                        </Field>
                    </div>
                </Section>

                {/* Tiers */}
                <Section title="Tiers" right={
                    <button onClick={addTier} className="console-btn-ghost" data-testid="tenant-add-tier">
                        <Plus size={12} className="inline mr-1" /> Add tier
                    </button>
                }>
                    <Field label="Default tier">
                        <select
                            className="console-input"
                            value={t.default_tier || ""}
                            onChange={(e) => setField("default_tier", e.target.value)}
                            data-testid="tenant-field-default-tier"
                        >
                            {(t.tiers || []).map((tier) => (
                                <option key={tier.tier_id} value={tier.tier_id}>
                                    {`${tier.name} (${tier.tier_id})`}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <div className="space-y-4 mt-4">
                        {(t.tiers || []).map((tier, idx) => (
                            <TierEditor
                                key={tier.tier_id}
                                tier={tier}
                                allTiers={t.tiers}
                                onChange={(updated) => {
                                    const next = deepClone(t);
                                    next.tiers[idx] = updated;
                                    if (t.default_tier === tier.tier_id && updated.tier_id !== tier.tier_id) {
                                        next.default_tier = updated.tier_id;
                                    }
                                    setT(next);
                                }}
                                onRemove={() => removeTier(tier.tier_id)}
                            />
                        ))}
                    </div>
                </Section>

                {/* Notes */}
                <Section title="Notes">
                    <textarea
                        className="console-input"
                        rows={4}
                        value={t.notes || ""}
                        onChange={(e) => setField("notes", e.target.value)}
                        data-testid="tenant-field-notes"
                    />
                </Section>

                {error && (
                    <p style={{ color: "var(--console-danger)", fontSize: 13 }} className="mt-4" data-testid="tenant-save-error">
                        {error}
                    </p>
                )}

                <div className="mt-8 flex items-center justify-end gap-3 pb-12">
                    <button onClick={onClose} className="console-btn-ghost" data-testid="tenant-drawer-cancel">
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={saving || !t.tenant_id || !t.name}
                        className="console-btn-primary"
                        data-testid="tenant-drawer-save"
                    >
                        <Save size={14} className="inline mr-2" />
                        {saving ? "Saving…" : "Save tenant"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TierEditor({ tier, allTiers, onChange, onRemove }) {
    const update = (patch) => onChange({ ...tier, ...patch });
    const togglePillar = (p) => {
        const has = (tier.pillars || []).includes(p);
        update({ pillars: has ? tier.pillars.filter((x) => x !== p) : [...(tier.pillars || []), p] });
    };
    const otherTiers = (allTiers || []).filter((x) => x.tier_id !== tier.tier_id);

    return (
        <div
            className="console-card p-5"
            data-testid={`tier-editor-${tier.tier_id}`}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="grid grid-cols-2 gap-3 flex-1 mr-4">
                    <Field label="Tier ID" hint="lowercase slug">
                        <input
                            className="console-input"
                            value={tier.tier_id}
                            onChange={(e) => update({ tier_id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                            data-testid={`tier-${tier.tier_id}-id`}
                        />
                    </Field>
                    <Field label="Tier name">
                        <input
                            className="console-input"
                            value={tier.name}
                            onChange={(e) => update({ name: e.target.value })}
                            data-testid={`tier-${tier.tier_id}-name`}
                        />
                    </Field>
                </div>
                {allTiers.length > 1 && (
                    <button onClick={onRemove} className="console-btn-ghost" style={{ color: "var(--console-danger)" }} data-testid={`tier-${tier.tier_id}-remove`}>
                        <Trash2 size={12} />
                    </button>
                )}
            </div>

            <Field label="Pillars granted">
                <div className="flex flex-wrap gap-2 mt-1">
                    {ALL_PILLARS.map((p) => {
                        const on = (tier.pillars || []).includes(p);
                        return (
                            <button
                                key={p}
                                onClick={() => togglePillar(p)}
                                className={`console-pill ${on ? "console-pill-active" : "console-pill-muted"}`}
                                style={{ cursor: "pointer", letterSpacing: "0.06em" }}
                                data-testid={`tier-${tier.tier_id}-pillar-${p}`}
                            >
                                {p}
                            </button>
                        );
                    })}
                </div>
            </Field>

            <div className="grid grid-cols-3 gap-3 mt-4">
                <Field label="Club access">
                    <label className="flex items-center gap-3" style={{ fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={!!tier.club_access}
                            onChange={(e) => update({ club_access: e.target.checked })}
                            className="console-switch"
                            data-testid={`tier-${tier.tier_id}-club-access`}
                        />
                        <span>{tier.club_access ? "Yes" : "No"}</span>
                    </label>
                </Field>
                <Field label="Monthly quota" hint="blank = unlimited">
                    <input
                        type="number"
                        className="console-input"
                        value={tier.monthly_quota ?? ""}
                        onChange={(e) => update({ monthly_quota: e.target.value === "" ? null : Number(e.target.value) })}
                        data-testid={`tier-${tier.tier_id}-quota`}
                    />
                </Field>
                <Field label="Upsell to" hint="empty = no upsell">
                    <select
                        className="console-input"
                        value={tier.upsell_to || ""}
                        onChange={(e) => update({ upsell_to: e.target.value || null })}
                        data-testid={`tier-${tier.tier_id}-upsell`}
                    >
                        <option value="">{"— none —"}</option>
                        {otherTiers.map((x) => (
                            <option key={x.tier_id} value={x.tier_id}>{x.name}</option>
                        ))}
                    </select>
                </Field>
            </div>

            <Field label="Perks" hint="comma-separated">
                <input
                    className="console-input"
                    value={(tier.perks || []).join(", ")}
                    onChange={(e) => update({ perks: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    data-testid={`tier-${tier.tier_id}-perks`}
                />
            </Field>

            <Field label="Member enrolment codes" hint="Comma-separated. Members visit /mira and enter one of these to auto-bind to this tier.">
                <input
                    className="console-input"
                    value={(tier.enrolment_codes || []).join(", ")}
                    onChange={(e) => update({ enrolment_codes: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
                    placeholder="ACC-BLACK-2026, ACC-EXEC-2026"
                    data-testid={`tier-${tier.tier_id}-enrolment-codes`}
                />
            </Field>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */

function Section({ title, right, children }) {
    return (
        <section className="mb-8" data-testid={`tenant-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-4">
                <p className="console-eyebrow" style={{ color: "var(--console-brass-bright)" }}>{title}</p>
                {right}
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="console-eyebrow block mb-1" style={{ fontSize: 10 }}>{label}</label>
            {children}
            {hint && (
                <p style={{ color: "var(--console-text-muted)", fontSize: 11, marginTop: 4 }}>{hint}</p>
            )}
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */

function newTenantTemplate() {
    return {
        tenant_id: "", name: "", active: true,
        contact: {}, brand: { display_mark: "Concierge®" },
        default_tier: "default",
        tiers: [{
            tier_id: "default", name: "Default",
            pillars: [...ALL_PILLARS], club_access: false,
            monthly_quota: null, perks: [], upsell_to: null,
        }],
        notes: "",
    };
}

function deepClone(o) { return JSON.parse(JSON.stringify(o)); }

function setByPath(obj, path, value) {
    const keys = path.split(".");
    let cur = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (cur[k] == null || typeof cur[k] !== "object") cur[k] = {};
        cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
}

function summarisePillarsAcrossTiers(tiers) {
    if (!tiers || !tiers.length) return "—";
    const counts = tiers.map((t) => (t.pillars || []).length);
    const max = Math.max(...counts);
    const min = Math.min(...counts);
    if (max === min) return `${max} / 11`;
    return `${min}–${max} / 11`;
}

function hasAnyClubAccess(tiers) {
    return (tiers || []).some((t) => t.club_access);
}

function summariseQuotas(tiers) {
    if (!tiers || !tiers.length) return "—";
    const parts = tiers.map((t) =>
        `${t.name}: ${t.monthly_quota == null ? "∞" : t.monthly_quota}`
    );
    return parts.join(" · ");
}
